// Time range picker for past game days: presets, an activity backdrop, drag handles and typed times.
// Applies as soon as a handle is released (no separate Apply step).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Icon from '../../../components/atoms/Icon';
import { formatClock, formatDuration } from '../live/liveMetrics';
import '../concession.css';

const MINUTE = 60 * 1000;
const MATCH_TOLERANCE = MINUTE;

// Busiest 60-minute stretch by average value
const findPeakHour = (activity, earliest, latest) => {
  if (!activity.length || latest - earliest <= 60 * MINUTE) return null;
  let best = null;
  let j = 0;
  let sum = 0;
  for (let i = 0; i < activity.length; i++) {
    sum += activity[i].y;
    while (activity[i].t - activity[j].t > 60 * MINUTE) {
      sum -= activity[j].y;
      j++;
    }
    const avg = sum / (i - j + 1);
    if (activity[i].t - activity[j].t >= 55 * MINUTE && (!best || avg > best.avg)) {
      best = { start: activity[j].t, end: activity[j].t + 60 * MINUTE, avg };
    }
  }
  if (!best) return null;
  return { start: Math.max(earliest, best.start), end: Math.min(latest, best.end) };
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const toTimeInput = (t) => moment(t).format('HH:mm');

// "HH:mm" on the same day as `base` (rolls past midnight if needed)
const fromTimeInput = (value, base) => {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  let t = d.getTime();
  if (t < base - 12 * 60 * MINUTE) t += 24 * 60 * MINUTE;
  return t;
};

const AnalysisWindow = ({ earliestTime, latestTime, onRangeChange, activity = [], gameStart, gatesOpen, target }) => {
  const earliest = earliestTime.getTime();
  const latest = latestTime.getTime();
  const span = Math.max(MINUTE, latest - earliest);

  const [range, setRange] = useState({ start: earliest, end: latest });
  const [dragging, setDragging] = useState(null); // 'start' | 'end' | null
  const trackRef = useRef(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  // Reset to the full day when the underlying day changes
  useEffect(() => {
    setRange({ start: earliest, end: latest });
    onRangeChange(new Date(earliest), new Date(latest));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earliest, latest]);

  const apply = (start, end) => {
    const next = { start: clamp(start, earliest, latest), end: clamp(end, earliest, latest) };
    if (next.end - next.start < MINUTE) return;
    setRange(next);
    onRangeChange(new Date(next.start), new Date(next.end));
  };

  // ---- Presets ----
  const presets = useMemo(() => {
    const inRange = (t) => t && t > earliest && t < latest;
    const peak = findPeakHour(activity, earliest, latest);
    return [
      { key: 'full', label: 'Full day', start: earliest, end: latest },
      // Pregame is always before gates open; gates open runs until game start
      {
        key: 'pregame',
        label: 'Pregame',
        start: earliest,
        end: gatesOpen,
        disabled: !inRange(gatesOpen),
      },
      {
        key: 'gates',
        label: 'Gates open',
        start: inRange(gatesOpen) ? gatesOpen : earliest,
        end: gameStart,
        disabled: !inRange(gameStart) || !(gatesOpen && gatesOpen < gameStart),
      },
      { key: 'in', label: 'In-game', start: gameStart, end: latest, disabled: !inRange(gameStart) },
      { key: 'peak', label: 'Peak hour', start: peak?.start, end: peak?.end, disabled: !peak },
    ];
  }, [activity, earliest, latest, gameStart, gatesOpen]);

  const activePreset = presets.find(
    (p) => !p.disabled && Math.abs(p.start - range.start) <= MATCH_TOLERANCE && Math.abs(p.end - range.end) <= MATCH_TOLERANCE
  );
  const isFull = activePreset?.key === 'full';

  // ---- Dragging ----
  const timeFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return Math.round((earliest + ratio * span) / MINUTE) * MINUTE;
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const t = timeFromClientX(e.clientX);
      setRange((r) =>
        dragging === 'start'
          ? { ...r, start: Math.min(t, r.end - MINUTE) }
          : { ...r, end: Math.max(t, r.start + MINUTE) }
      );
    };
    const onUp = () => {
      setDragging(null);
      const r = rangeRef.current;
      onRangeChange(new Date(r.start), new Date(r.end));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const handleTrackPointerDown = (e) => {
    if (e.target.closest('.cx-aw-handle')) return;
    const t = timeFromClientX(e.clientX);
    const handle = Math.abs(t - range.start) < Math.abs(t - range.end) ? 'start' : 'end';
    setRange((r) =>
      handle === 'start' ? { ...r, start: Math.min(t, r.end - MINUTE) } : { ...r, end: Math.max(t, r.start + MINUTE) }
    );
    setDragging(handle);
  };

  const handleKey = (handle) => (e) => {
    const step = (e.shiftKey ? 15 : 5) * MINUTE;
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
    if (!delta) return;
    e.preventDefault();
    if (handle === 'start') apply(Math.min(range.start + delta, range.end - MINUTE), range.end);
    else apply(range.start, Math.max(range.end + delta, range.start + MINUTE));
  };

  // ---- Backdrop geometry ----
  const pct = (t) => ((t - earliest) / span) * 100;
  const maxY = Math.max(target || 0, ...activity.map((p) => p.y), 1) * 1.15;
  const areaPoints = activity.length > 1
    ? `0,100 ${activity.map((p) => `${pct(p.t).toFixed(2)},${(100 - (p.y / maxY) * 100).toFixed(2)}`).join(' ')} 100,100`
    : null;

  const hourTicks = [];
  const firstHour = moment(earliest).startOf('hour').add(1, 'hour').valueOf();
  const tickStep = span > 8 * 60 * MINUTE ? 2 : 1;
  for (let t = firstHour; t < latest; t += tickStep * 60 * MINUTE) hourTicks.push(t);

  // Contiguous overflow stretches in the activity series
  const overflowSpans = [];
  activity.forEach((p, i) => {
    if (!p.o) return;
    const last = overflowSpans[overflowSpans.length - 1];
    const end = activity[i + 1] ? activity[i + 1].t : p.t + MINUTE;
    if (last && i > 0 && activity[i - 1].o) last.end = end;
    else overflowSpans.push({ start: p.t, end });
  });

  const markers = [
    gatesOpen && gatesOpen > earliest && gatesOpen < latest && { t: gatesOpen, label: 'Gates' },
    gameStart && gameStart > earliest && gameStart < latest && { t: gameStart, label: 'Game start' },
  ].filter(Boolean);

  return (
    <div className="cx-aw">
      {/* Presets + typed times */}
      <div className="cx-aw-top">
        <div className="cx-chart-segmented" role="group" aria-label="Time range presets">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              disabled={p.disabled}
              className={`cx-chart-segment ${activePreset?.key === p.key ? 'active' : ''}`}
              onClick={() => apply(p.start, p.end)}
              title={p.disabled ? 'Not available for this game' : `${formatClock(p.start)} – ${formatClock(p.end)}`}
            >
              {p.label}
            </button>
          ))}
          <span className={`cx-chart-segment cx-aw-custom ${!activePreset ? 'active' : ''}`}>Custom</span>
        </div>

        <div className="cx-aw-inputs">
          <label className="cx-field">
            <span>From</span>
            <input
              type="time"
              className="form-control form-control-sm"
              value={toTimeInput(range.start)}
              onChange={(e) => {
                const t = fromTimeInput(e.target.value, earliest);
                if (t !== null && t >= earliest && t < range.end) apply(t, range.end);
              }}
            />
          </label>
          <label className="cx-field">
            <span>To</span>
            <input
              type="time"
              className="form-control form-control-sm"
              value={toTimeInput(range.end)}
              onChange={(e) => {
                const t = fromTimeInput(e.target.value, earliest);
                if (t !== null && t <= latest && t > range.start) apply(range.start, t);
              }}
            />
          </label>
        </div>
      </div>

      {/* Track with activity backdrop */}
      <div className="cx-aw-track-wrap">
        <div ref={trackRef} className={`cx-aw-track ${dragging ? 'dragging' : ''}`} onPointerDown={handleTrackPointerDown}>
          {areaPoints && (
            <svg className="cx-aw-activity" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <polygon points={areaPoints} />
              {target !== undefined && (
                <line x1="0" x2="100" y1={100 - (target / maxY) * 100} y2={100 - (target / maxY) * 100} vectorEffect="non-scaling-stroke" />
              )}
            </svg>
          )}

          {/* Overflow periods */}
          {overflowSpans.map((span) => (
            <div
              key={span.start}
              className="cx-aw-overflow"
              style={{ left: `${pct(span.start)}%`, width: `${Math.max(0.4, pct(span.end) - pct(span.start))}%` }}
              title={`Overflow ${formatClock(span.start)} – ${formatClock(span.end)}`}
            />
          ))}

          {/* Dim outside the selection */}
          <div className="cx-aw-dim" style={{ left: 0, width: `${pct(range.start)}%` }} />
          <div className="cx-aw-dim" style={{ left: `${pct(range.end)}%`, right: 0 }} />
          <div className="cx-aw-selection" style={{ left: `${pct(range.start)}%`, width: `${pct(range.end) - pct(range.start)}%` }} />

          {markers.map((m) => (
            <div key={m.label} className="cx-aw-marker" style={{ left: `${pct(m.t)}%` }}>
              <span>{m.label}</span>
            </div>
          ))}

          {['start', 'end'].map((handle) => (
            <div
              key={handle}
              role="slider"
              tabIndex={0}
              aria-label={handle === 'start' ? 'Range start' : 'Range end'}
              aria-valuemin={earliest}
              aria-valuemax={latest}
              aria-valuenow={range[handle]}
              aria-valuetext={formatClock(range[handle])}
              className="cx-aw-handle"
              style={{ left: `${pct(range[handle])}%` }}
              onPointerDown={(e) => {
                e.preventDefault();
                setDragging(handle);
              }}
              onKeyDown={handleKey(handle)}
            >
              <span className="cx-aw-handle-label">{formatClock(range[handle])}</span>
            </div>
          ))}
        </div>

        <div className="cx-aw-axis">
          <span style={{ left: '0%' }}>{formatClock(earliest)}</span>
          {hourTicks
            .filter((t) => pct(t) > 6 && pct(t) < 94)
            .map((t) => (
              <span key={t} style={{ left: `${pct(t)}%` }}>{moment(t).format('h A')}</span>
            ))}
          <span style={{ left: '100%' }}>{formatClock(latest)}</span>
        </div>
      </div>

      {/* Readout */}
      <div className="cx-aw-readout">
        <span>
          <Icon name="clock" /> {formatClock(range.start)} – {formatClock(range.end)}
          <span className="cx-aw-duration">{formatDuration((range.end - range.start) / MINUTE)}</span>
          {overflowSpans.length > 0 && (
            <span className="cx-aw-legend">
              <span className="cx-aw-legend-swatch" /> Overflow
            </span>
          )}
        </span>
        {!isFull && (
          <button type="button" className="cx-sort-btn" onClick={() => apply(earliest, latest)}>
            <Icon name="arrow-counterclockwise" />
            Reset to full day
          </button>
        )}
      </div>
    </div>
  );
};

export default AnalysisWindow;
